import { nullableArray, nullableOptional } from '@shigo-ui/api-client';
import { z } from 'zod';

export enum OwnerStatus {
  Active = 'ACTIVE',
  Inactive = 'INACTIVE',
  Archived = 'ARCHIVED',
}

export enum SellersJsonStatus {
  Valid = 'VALID',
  RefetchRequired = 'REFETCH_REQUIRED',
  Invalid = 'INVALID',
  Unknown = 'UNKNOWN',
}

export enum SchainLogicMode {
  DirectOnly = 'DIRECT_ONLY',
  ResellerPriority = 'RESELLER_PRIORITY',
  DirectPriority = 'DIRECT_PRIORITY',
  ResellerOnly = 'RESELLER_ONLY',
}

export enum OwnerSortProperty {
  Name = 'name',
  BusinessDomain = 'businessDomain',
}

export enum OwnerSortDirection {
  Asc = 'ASC',
  Desc = 'DESC',
}

export enum OwnerEditorTab {
  Identity = 'IDENTITY',
  Schain = 'SCHAIN',
  Compliance = 'COMPLIANCE',
  Partners = 'PARTNERS',
}

export enum OwnerSchainDataset {
  Direct = 'DIRECT',
  Reseller = 'RESELLER',
}

export enum OwnerAdsTxtRelationshipType {
  Direct = 'DIRECT',
  Reseller = 'RESELLER',
}

const ownerStatusSchema = z.preprocess(
  (value) => (value === null || value === undefined ? OwnerStatus.Active : value),
  z.enum(OwnerStatus),
);

const sellersJsonStatusSchema = z.preprocess(
  (value) => (value === null || value === undefined ? SellersJsonStatus.Unknown : value),
  z.enum(SellersJsonStatus),
);

const schainLogicModeSchema = z.preprocess(
  (value) => (value === null || value === undefined ? SchainLogicMode.ResellerPriority : value),
  z.enum(SchainLogicMode),
);

export const ownerDirectSchainRecordSchema = z.object({
  id: nullableOptional(z.coerce.string()),
  bundle: z.string(),
  incomingFirstHopAsi: z.string(),
  directHp: nullableOptional(z.number()),
  directSid: nullableOptional(z.string()),
});

export const ownerResellerSchainRecordSchema = z.object({
  id: nullableOptional(z.coerce.string()),
  bundle: z.string(),
  incomingFirstHopAsi: z.string(),
  resellerSid: nullableOptional(z.string()),
});

export const ownerAdsTxtRecordSchema = z.looseObject({
  id: nullableOptional(z.coerce.string()),
  domain: z.string(),
  publisherId: z.string(),
  relationshipType: z.enum(OwnerAdsTxtRelationshipType),
  certificationId: nullableOptional(z.string()),
});

export const ownerDemandPartnerSchema = z.looseObject({
  id: z.coerce.string(),
  displayName: z.string(),
  bidderCode: nullableOptional(z.string()),
  status: nullableOptional(z.string()),
  demandIntegrationCnt: nullableOptional(z.number().int().nonnegative()),
  demandIntegrationsCnt: nullableOptional(z.number().int().nonnegative()),
  pricingType: nullableOptional(z.string()),
});

export const ownerSchema = z.looseObject({
  id: nullableOptional(z.coerce.string()),
  name: z.string(),
  status: ownerStatusSchema,
  businessDomain: nullableOptional(z.string()),
  sellersJsonUrl: nullableOptional(z.string()),
  sellersJsonStatus: sellersJsonStatusSchema,
  sellersJsonLastRefreshed: nullableOptional(z.string()),
  sellersJsonCache: nullableOptional(z.string()),
  sellersJsonFailReason: nullableOptional(z.string()),
  tcfVendorId: nullableOptional(z.coerce.string()),
  resellerBundleAllowlist: nullableOptional(z.string()),
  billingEntity: nullableOptional(z.string()),
  paymentCycle: nullableOptional(z.string()),
  billingContactEmail: nullableOptional(z.string()),
  demandPartnerCount: nullableOptional(z.number().int().nonnegative()),
  integrationCount: nullableOptional(z.number().int().nonnegative()),
  defaultSchainLogicMode: schainLogicModeSchema,
  rev7d: nullableOptional(z.number()),
  directSchainRecords: nullableArray(ownerDirectSchainRecordSchema),
  resellerSchainRecords: nullableArray(ownerResellerSchainRecordSchema),
  adsTxtRecords: nullableArray(ownerAdsTxtRecordSchema),
  demandPartners: nullableArray(ownerDemandPartnerSchema),
  connectedDemandPartners: nullableArray(ownerDemandPartnerSchema),
});

export const ownerListItemSchema = ownerSchema.extend({
  id: z.union([z.string(), z.number()]).transform(String).pipe(z.string().min(1)),
});

export const ownersPageSchema = z.looseObject({
  content: z.array(ownerListItemSchema),
  totalElements: z.number().int().nonnegative(),
  totalPages: z.number().int().nonnegative().optional(),
  number: z.number().int().nonnegative().optional(),
  size: z.number().int().positive().optional(),
});

export const ownerSummarySchema = z.preprocess(
  (value) => value ?? {},
  z.looseObject({
    ownersNo: z.number().int().nonnegative().optional().default(0),
    dpNo: z.number().int().nonnegative().optional().default(0),
    dpcNo: z.number().int().nonnegative().optional().default(0),
    rev7d: z.number().optional().default(0),
  }),
);

export const sellersJsonStatusResultSchema = z.looseObject({
  valid: nullableOptional(z.boolean()),
  url: nullableOptional(z.string()),
  status: sellersJsonStatusSchema,
  lastRefreshed: nullableOptional(z.string()),
  failReason: nullableOptional(z.string()),
});

export interface OwnersFilter {
  searchTerm: string;
  page: number;
  size: number;
  sortProperty: OwnerSortProperty | null;
  sortDirection: OwnerSortDirection;
}

export interface OwnerMutationPayload {
  id?: string;
  name: string;
  status: OwnerStatus;
  businessDomain: string | null;
  sellersJsonUrl: string | null;
  sellersJsonStatus: SellersJsonStatus;
  sellersJsonLastRefreshed: string | null;
  sellersJsonCache: string | null;
  sellersJsonFailReason: string | null;
  tcfVendorId: string | null;
  resellerBundleAllowlist: string | null;
  billingEntity: string | null;
  paymentCycle: string | null;
  billingContactEmail: string | null;
  defaultSchainLogicMode: SchainLogicMode;
  directSchainRecords: OwnerDirectSchainRecord[];
  resellerSchainRecords: OwnerResellerSchainRecord[];
  adsTxtRecords: OwnerAdsTxtRecord[];
  demandPartners: OwnerDemandPartner[];
  connectedDemandPartners: OwnerDemandPartner[];
  clearTcfVendorId?: true;
}

export type Owner = z.infer<typeof ownerSchema>;
export type OwnerListItem = z.infer<typeof ownerListItemSchema>;
export type OwnerDirectSchainRecord = z.infer<typeof ownerDirectSchainRecordSchema>;
export type OwnerResellerSchainRecord = z.infer<typeof ownerResellerSchainRecordSchema>;
export type OwnerAdsTxtRecord = z.infer<typeof ownerAdsTxtRecordSchema>;
export type OwnerDemandPartner = z.infer<typeof ownerDemandPartnerSchema>;
export type OwnerSummary = z.infer<typeof ownerSummarySchema>;

export const ownerPageSizes = [5, 10, 25, 50, 100] as const;

export const defaultOwnersFilter: OwnersFilter = {
  searchTerm: '',
  page: 0,
  size: 10,
  sortProperty: OwnerSortProperty.Name,
  sortDirection: OwnerSortDirection.Asc,
};

export function isOwnerSortProperty(value: string): value is OwnerSortProperty {
  return Object.values(OwnerSortProperty).includes(value as OwnerSortProperty);
}

export function formatOwnerRevenue(value?: number | null) {
  if (value === undefined || value === null) return '-';
  if (Math.abs(value) >= 1000) return `$${(value / 1000).toFixed(1)}K`;
  return `$${value.toFixed(2)}`;
}
